import { API_BASE_URL, API_CONFIG } from '../config/api';
import type {
  ApiResponse,
  CheckInTodayResponse,
  CreateCheckInInput,
  AssessmentSheetUpdate,
  CreateManualAlertInput,
  GradeSheet,
  Gradebook,
  StudentGradebook,
  ShiftType,
  ClassShiftTypeChange,
  HomeworkSheetRecord,
  DashboardNotificationItem,
  DashboardNotificationResponse,
  MakeupStudentCandidate,
  ReportCatalog,
  ReportFilterValues,
  ReportPreview,
  DashboardNotificationSeverity,
  HomeworkAlertDetail,
  HomeworkAlertFilters,
  HomeworkAlertListResponse,
  HomeworkAlertStatus,
  ManualAlertDetail,
  ManualAlertFilters,
  ManualAlertListResponse,
  ManualAlertStatus,
  PayrollAdjustmentPatch,
  PayrollSlot,
  PayrollSummary,
  PayrollRateChange,
  PersonPayroll,
  SessionSlot,
  StaffActivity,
  StaffCheckIn,
} from '../types';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export class ApiError extends Error {
    status: number;
    data: unknown;
    response: { data: unknown; status: number };

    constructor(message: string, status: number, data: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
        this.response = { data, status };
    }
}

async function parseResponse(response: Response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function request<T = any>(method: HttpMethod, url: string, data?: unknown): Promise<{ data: T }> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), API_CONFIG.getTimeout());

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method,
      headers,
      signal: controller.signal,
      body: data === undefined ? undefined : JSON.stringify(data),
    });

    const parsed = await parseResponse(response);
    if (!response.ok) {
      const message =
        typeof parsed === 'object' && parsed && 'message' in parsed
          ? String((parsed as { message?: unknown }).message)
          : 'Request failed';
      throw new ApiError(message, response.status, parsed);
    }

    return { data: parsed as T };
  } finally {
    window.clearTimeout(timeout);
  }
}

// A file the server builds (e.g. an Excel export): its contents and the name the server gave it.
async function downloadFile(url: string): Promise<{ blob: Blob; fileName: string | null }> {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    const parsed = await parseResponse(response);
    const message = typeof parsed === 'object' && parsed && 'message' in parsed ? String((parsed as { message?: unknown }).message) : 'Tải file thất bại';
    throw new ApiError(message, response.status, parsed);
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const plain = /filename="([^"]+)"/i.exec(disposition)?.[1];
  return { blob: await response.blob(), fileName: encoded ? decodeURIComponent(encoded) : plain ?? null };
}

const apiClient = {
  get: <T = any>(url: string) => request<T>('GET', url),
  post: <T = any>(url: string, data?: unknown) => request<T>('POST', url, data),
  put: <T = any>(url: string, data?: unknown) => request<T>('PUT', url, data),
  delete: <T = any>(url: string, data?: unknown) => request<T>('DELETE', url, data),
  request,
};

export function buildHomeworkAlertQuery(filters: HomeworkAlertFilters = {}): string {
  const entries: Array<[string, string]> = [];
  const append = (key: string, value: string | number | undefined) => {
    if (value === undefined || (typeof value === 'string' && value.trim() === '')) return;
    entries.push([key, typeof value === 'string' ? value.trim() : String(value)]);
  };

  append('status', filters.status);
  append('search', filters.search);
  append('campusId', filters.campusId);
  append('classId', filters.classId);
  append('page', filters.page);
  append('pageSize', filters.pageSize);

  return entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

export function buildManualAlertQuery(filters: ManualAlertFilters = {}): string {
  const entries: Array<[string, string]> = [];
  const append = (key: string, value: string | number | undefined) => {
    if (value === undefined || (typeof value === 'string' && value.trim() === '')) return;
    entries.push([key, typeof value === 'string' ? value.trim() : String(value)]);
  };

  append('status', filters.status);
  append('severity', filters.severity);
  append('source', filters.source);
  append('search', filters.search);
  append('campusId', filters.campusId);
  append('classId', filters.classId);
  append('page', filters.page);
  append('pageSize', filters.pageSize);

  return entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

// Auth API
export const authApi = {
    login: (credentials: any) =>
        apiClient.post('/auth/login', credentials),

    register: (userData: any) =>
        apiClient.post('/auth/register', userData),

    changePassword: (data: { currentPassword: string; newPassword: string }) =>
        apiClient.post('/auth/change-password', data),

    getMe: () =>
        apiClient.get('/auth/me'),

    updateMe: (data: { firstName: string; lastName: string; phone: string | null }) =>
        apiClient.put('/auth/me', data),
};

export const dashboardApi = {
    getSummary: () =>
        apiClient.get('/dashboard/summary'),

    // full: the notifications page, which lists more homework and manual alerts than the bell.
    getNotifications: (options: { full?: boolean } = {}) =>
        apiClient.get<ApiResponse<DashboardNotificationResponse>>(`/dashboard/notifications${options.full ? '?full=1' : ''}`),

    // A "Nhật ký nhân sự" entry as a notification, even once it has left the list.
    getStaffNotification: (activityId: string) =>
        apiClient.get<ApiResponse<DashboardNotificationItem>>(`/dashboard/notifications/staff/${encodeURIComponent(activityId)}`),

    markNotificationsRead: (ids: string[], read = true) =>
        apiClient.put('/dashboard/notifications/read', { ids, read }),

    starNotification: (id: string, starred: boolean) =>
        apiClient.put('/dashboard/notifications/star', { id, starred }),

    // null goes back to the default priority.
    setNotificationPriority: (id: string, severity: DashboardNotificationSeverity | null) =>
        apiClient.put('/dashboard/notifications/priority', { id, severity }),

    getHomeworkAlerts: (filters: HomeworkAlertFilters = {}) => {
        const query = buildHomeworkAlertQuery(filters);
        return apiClient.get<ApiResponse<HomeworkAlertListResponse>>(
            `/dashboard/homework-alerts${query ? `?${query}` : ''}`,
        );
    },

    getHomeworkAlert: (alertId: string) =>
        apiClient.get<ApiResponse<HomeworkAlertDetail>>(
            `/dashboard/homework-alerts/${encodeURIComponent(alertId)}`,
        ),

    updateHomeworkAlertStatus: (alertId: string, status: HomeworkAlertStatus) =>
        apiClient.put<ApiResponse<HomeworkAlertDetail>>(
            `/dashboard/homework-alerts/${encodeURIComponent(alertId)}/status`,
            { status },
        ),

    addHomeworkAlertNote: (alertId: string, note: string) =>
        apiClient.post<ApiResponse<HomeworkAlertDetail>>(
            `/dashboard/homework-alerts/${encodeURIComponent(alertId)}/notes`,
            { note },
        ),

    getManualAlerts: (filters: ManualAlertFilters = {}) => {
        const query = buildManualAlertQuery(filters);
        return apiClient.get<ApiResponse<ManualAlertListResponse>>(
            `/dashboard/manual-alerts${query ? `?${query}` : ''}`,
        );
    },

    createManualAlert: (input: CreateManualAlertInput) =>
        apiClient.post<ApiResponse<ManualAlertDetail>>('/dashboard/manual-alerts', input),

    getManualAlert: (alertId: string) =>
        apiClient.get<ApiResponse<ManualAlertDetail>>(
            `/dashboard/manual-alerts/${encodeURIComponent(alertId)}`,
        ),

    updateManualAlertStatus: (alertId: string, status: ManualAlertStatus) =>
        apiClient.put<ApiResponse<ManualAlertDetail>>(
            `/dashboard/manual-alerts/${encodeURIComponent(alertId)}/status`,
            { status },
        ),

    addManualAlertNote: (alertId: string, note: string) =>
        apiClient.post<ApiResponse<ManualAlertDetail>>(
            `/dashboard/manual-alerts/${encodeURIComponent(alertId)}/notes`,
            { note },
        ),
};

// Users API
export const usersApi = {
    getAll: () =>
        apiClient.get('/users'),

    getManagers: () =>
        apiClient.get('/users/managers'),

    getById: (id: string) =>
        apiClient.get(`/users/${id}`),

    update: (id: string, userData: any) =>
        apiClient.put(`/users/${id}`, userData),

    delete: (id: string) =>
        apiClient.delete(`/users/${id}`),

    create: (userData: any) =>
        apiClient.post('/users', userData),

    changePassword: (id: string, newPassword: string) =>
        apiClient.put(`/users/${id}/change-password`, { newPassword }),

    toggleStatus: (id: string) =>
        apiClient.put(`/users/${id}/toggle-status`, {}),
};

// Campus API
export const campusApi = {
    getAll: () =>
        apiClient.get('/campuses'),

    getById: (id: string) =>
        apiClient.get(`/campuses/${id}`),

    create: (campusData: any) =>
        apiClient.post('/campuses', campusData),

    update: (id: string, campusData: any) =>
        apiClient.put(`/campuses/${id}`, campusData),

    delete: (id: string) =>
        apiClient.delete(`/campuses/${id}`),
};

// Classes API
export const classesApi = {
    getAll: () =>
        apiClient.get('/classes'),

    getById: (id: string) =>
        apiClient.get(`/classes/${id}`),

    getGradebook: (id: string, range: { from: string; to: string }) =>
        apiClient.get<{ data: Gradebook }>(`/classes/${id}/gradebook?from=${range.from}&to=${range.to}`),

    getByCampus: (campusId: string) =>
        apiClient.get(`/classes/campus/${campusId}`),

    create: (classData: any) =>
        apiClient.post('/classes', classData),

    update: (id: string, classData: any) =>
        apiClient.put(`/classes/${id}`, classData),

    delete: (id: string) =>
        apiClient.delete(`/classes/${id}`),

    // Student enrollment methods
    getStudents: (classId: string) =>
        apiClient.get(`/classes/${classId}/students`),

    enrollStudent: (classId: string, studentId: string) =>
        apiClient.post(`/classes/${classId}/enroll`, { studentId }),

    removeStudent: (classId: string, studentId: string) =>
        apiClient.delete(`/classes/${classId}/students/${studentId}`),

    bulkImport: (classesData: any[]) =>
        apiClient.post('/classes/bulk-import', { classes: classesData }),
};

// Students API
export const studentsApi = {
    getAll: () =>
        apiClient.get('/students'),

    getById: (id: string) =>
        apiClient.get(`/students/${id}`),

    getGradebook: (id: string, range: { from: string; to: string }) =>
        apiClient.get<{ data: StudentGradebook }>(`/students/${id}/gradebook?from=${range.from}&to=${range.to}`),

    create: (studentData: any) =>
        apiClient.post('/students', studentData),

    update: (id: string, studentData: any) =>
        apiClient.put(`/students/${id}`, studentData),

    delete: (id: string) =>
        apiClient.delete(`/students/${id}`),

    bulkDelete: (studentIds: string[]) =>
        apiClient.delete('/students/bulk', { studentIds }),

    deleteAll: () =>
        apiClient.delete('/students', { all: true }),

    bulkImport: (studentsData: any[]) =>
        apiClient.post('/students/bulk-import', { students: studentsData }),
};

// Attendance API
export const attendanceApi = {
    getAll: (params?: { date?: string; classId?: string; campusId?: string; scope?: 'mine' }) => {
        const query = new URLSearchParams();
        if (params?.date) query.append('date', params.date);
        if (params?.classId) query.append('classId', params.classId);
        if (params?.campusId) query.append('campusId', params.campusId);
        if (params?.scope) query.append('scope', params.scope);
        return apiClient.get(`/attendance${query.toString() ? `?${query.toString()}` : ''}`);
    },

    getByClass: (classId: string, date?: string) =>
        apiClient.get(`/attendance/class/${classId}${date ? `?date=${date}` : ''}`),

    // Students to add to a session as a make-up: names and current classes, a few at a time.
    searchMakeupStudents: (classId: string, date: string, search: string) =>
        apiClient.get<{ data: MakeupStudentCandidate[] }>(
            `/attendance/class/${classId}/makeup-students?${new URLSearchParams({ date, search }).toString()}`,
        ),

    markAttendance: (attendanceData: any) =>
        apiClient.post('/attendance', attendanceData),

    markStudentAttendance: (studentAttendanceData: any) =>
        apiClient.post('/attendance/mark', studentAttendanceData),

    register: (data: { classId: string; slot: SessionSlot; force?: boolean; reason?: string | null; confirmOverlap?: boolean }) =>
        apiClient.post('/attendance/registrations', data),

    cancelRegistration: (attendanceId: string, slot: SessionSlot) =>
        apiClient.post(`/attendance/${attendanceId}/registrations/${slot}/cancel`),

    updateStaffTimes: (attendanceId: string, data: { slot: SessionSlot; startTime: string; endTime: string }) =>
        apiClient.put(`/attendance/${attendanceId}/staff-times`, data),

    getGrades: (attendanceId: string) =>
        apiClient.get<{ data: GradeSheet & { session: { id: string; classId: string; className: string; date: string } } }>(`/attendance/${attendanceId}/grades`),

    saveHomework: (attendanceId: string, records: HomeworkSheetRecord[]) =>
        apiClient.put<{ data: GradeSheet }>(`/attendance/${attendanceId}/homework`, { records }),

    createAssessment: (attendanceId: string, data: { name: string; weight: number }) =>
        apiClient.post<{ data: GradeSheet }>(`/attendance/${attendanceId}/assessments`, data),

    saveAssessments: (attendanceId: string, assessments: AssessmentSheetUpdate[]) =>
        apiClient.put<{ data: GradeSheet }>(`/attendance/${attendanceId}/assessments`, { assessments }),

    deleteAssessment: (attendanceId: string, assessmentId: string) =>
        apiClient.delete<{ data: GradeSheet }>(`/attendance/${attendanceId}/assessments/${assessmentId}`),

    getStats: (params?: { startDate?: string; endDate?: string; classId?: string; campusId?: string }) => {
        const query = new URLSearchParams();
        if (params?.startDate) query.append('startDate', params.startDate);
        if (params?.endDate) query.append('endDate', params.endDate);
        if (params?.classId) query.append('classId', params.classId);
        if (params?.campusId) query.append('campusId', params.campusId);
        return apiClient.get(`/attendance/stats${query.toString() ? `?${query.toString()}` : ''}`);
    },


    updateAttendance: (id: string, attendanceData: any) =>
        apiClient.put(`/attendance/${id}`, attendanceData),
};

// Payments API
export const paymentsApi = {
    getAll: () =>
        apiClient.get('/payments'),

    getByStudent: (studentId: string) =>
        apiClient.get(`/payments/student/${studentId}`),

    create: (paymentData: any) =>
        apiClient.post('/payments', paymentData),

    update: (id: string, paymentData: any) =>
        apiClient.put(`/payments/${id}`, paymentData),

    delete: (id: string) =>
        apiClient.delete(`/payments/${id}`),
};

// Subjects API
export const subjectsApi = {
    getAll: () =>
        apiClient.get('/subjects'),

    getById: (id: string) =>
        apiClient.get(`/subjects/${id}`),

    create: (data: any) =>
        apiClient.post('/subjects', data),

    update: (id: string, data: any) =>
        apiClient.put(`/subjects/${id}`, data),

    delete: (id: string) =>
        apiClient.delete(`/subjects/${id}`),
};

// Permissions API
export const permissionsApi = {
    // Get current user's permissions
    getMyPermissions: () =>
        apiClient.get('/permissions/me'),

    // Get user's permissions (admin only)
    getUserPermissions: (userId: string) =>
        apiClient.get(`/permissions/user/${userId}`),

    // Get all available permissions
    getAllPermissions: () =>
        apiClient.get('/permissions/all'),

    // Get role default permissions
    getRolePermissions: (role: string) =>
        apiClient.get(`/permissions/role/${role}`),

    // Grant permission to user
    grantPermission: (userId: string, data: { resource: string; action: string; reason?: string; expiresAt?: string }) =>
        apiClient.post(`/permissions/user/${userId}/grant`, data),

    // Revoke permission from user
    revokePermission: (userId: string, data: { resource: string; action: string; reason?: string }) =>
        apiClient.post(`/permissions/user/${userId}/revoke`, data),

    // Remove permission override (revert to role default)
    removePermissionOverride: (userId: string, data: { resource: string; action: string }) =>
        apiClient.delete(`/permissions/user/${userId}/remove`, data),

    // Bulk update user permissions
    bulkUpdatePermissions: (userId: string, data: { permissions: any[]; reason?: string }) =>
        apiClient.put(`/permissions/user/${userId}/bulk`, data),
};

// Teachers API
export const teachersApi = {
    // Get all teachers and assistants
    getAll: () =>
        apiClient.get('/teachers'),

    // Get teacher details
    getById: (id: string) =>
        apiClient.get(`/teachers/${id}`),

    // Get teachers statistics
    getStats: () =>
        apiClient.get('/teachers/stats/overview'),
};

export const checkInsApi = {
    getToday: () =>
        apiClient.get<{ data: CheckInTodayResponse }>('/check-ins/today'),

    create: (data: CreateCheckInInput) =>
        apiClient.post<{ data: StaffCheckIn }>('/check-ins', data),

    getActivities: (params: { date?: string; campusId?: string; classId?: string; userId?: string; needsReview?: boolean } = {}) => {
        const query = new URLSearchParams();
        if (params.date) query.append('date', params.date);
        if (params.campusId) query.append('campusId', params.campusId);
        if (params.classId) query.append('classId', params.classId);
        if (params.userId) query.append('userId', params.userId);
        if (params.needsReview) query.append('needsReview', 'true');
        const suffix = query.toString() ? `?${query.toString()}` : '';
        return apiClient.get<{ data: StaffActivity[] }>(`/check-ins/activities${suffix}`);
    },

    markReviewed: (activityId: string) =>
        apiClient.post(`/check-ins/activities/${activityId}/review`),
};

const rangeQuery = (range: { from?: string; to?: string }) => {
    const query = new URLSearchParams();
    if (range.from) query.append('from', range.from);
    if (range.to) query.append('to', range.to);
    return query.toString() ? `?${query.toString()}` : '';
};

export const payrollApi = {
    getMine: (range: { from?: string; to?: string } = {}) =>
        apiClient.get<{ data: PersonPayroll }>(`/payroll/me${rangeQuery(range)}`),

    getSummary: (range: { from?: string; to?: string } = {}) =>
        apiClient.get<{ data: PayrollSummary }>(`/payroll/summary${rangeQuery(range)}`),

    getPerson: (userId: string, range: { from?: string; to?: string } = {}) =>
        apiClient.get<{ data: PersonPayroll }>(`/payroll/users/${userId}${rangeQuery(range)}`),

    getRateChanges: (userId: string) =>
        apiClient.get<{ data: PayrollRateChange[] }>(`/payroll/users/${userId}/rate-changes`),

    addRateChange: (userId: string, data: { slot: PayrollSlot; shiftTypeId: string; effectiveFrom: string; rate: number }) =>
        apiClient.post<{ data: PayrollRateChange[] }>(`/payroll/users/${userId}/rate-changes`, data),

    getShiftTypes: () =>
        apiClient.get<{ data: ShiftType[] }>('/payroll/shift-types'),

    createShiftType: (data: { name: string; minutes: number }) =>
        apiClient.post<{ data: ShiftType[] }>('/payroll/shift-types', data),

    updateShiftType: (id: string, data: Partial<{ name: string; minutes: number; isActive: boolean }>) =>
        apiClient.put<{ data: ShiftType[] }>(`/payroll/shift-types/${id}`, data),

    getClassShiftTypes: (classId: string) =>
        apiClient.get<{ data: ClassShiftTypeChange[] }>(`/payroll/classes/${classId}/shift-types`),

    setClassShiftType: (classId: string, data: { shiftTypeId: string; effectiveFrom: string }) =>
        apiClient.post<{ data: ClassShiftTypeChange[] }>(`/payroll/classes/${classId}/shift-types`, data),

    deleteClassShiftType: (classId: string, changeId: string) =>
        apiClient.delete<{ data: ClassShiftTypeChange[] }>(`/payroll/classes/${classId}/shift-types/${changeId}`),

    deleteRateChange: (userId: string, changeId: string) =>
        apiClient.delete<{ data: PayrollRateChange[] }>(`/payroll/users/${userId}/rate-changes/${changeId}`),

    adjustShift: (attendanceId: string, slot: PayrollSlot, data: PayrollAdjustmentPatch) =>
        apiClient.put(`/payroll/shifts/${attendanceId}/${slot}`, data),
};

// Export apiClient for general use
export const api = apiClient;

function reportQuery(filters: ReportFilterValues) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        if (value) params.set(key, String(value));
    }
    const query = params.toString();
    return query ? `?${query}` : '';
}

export const reportsApi = {
    getCatalog: () => apiClient.get<{ data: ReportCatalog }>('/reports'),
    preview: (reportId: string, filters: ReportFilterValues) =>
        apiClient.get<{ data: ReportPreview }>(`/reports/${encodeURIComponent(reportId)}/preview${reportQuery(filters)}`),
    download: (reportId: string, filters: ReportFilterValues) =>
        downloadFile(`/reports/${encodeURIComponent(reportId)}/download${reportQuery(filters)}`),
};
